import {
  LMS_CODE,
  QUEUE_STATUS,
  QUEUE_VIEW,
  REVIEW_STATUS,
  SUGGESTED_RESOURCE_TAG,
  getReviewStatusKey,
  isResolvedStatus,
} from '../constants/lms-course-review.constants';
import {
  SCORE_TIER,
  SCORE_TIER_THRESHOLDS,
} from '../constants/score-tiers.constants';

// --- Status ---

export const isResolvedReviewStatus = (reviewStatus) =>
  isResolvedStatus(reviewStatus);

export const toUiReviewStatus = (reviewStatus) => {
  if (reviewStatus === REVIEW_STATUS.PENDING.key) return QUEUE_STATUS.PENDING;
  if (isResolvedReviewStatus(reviewStatus)) return QUEUE_STATUS.RESOLVED;
  return reviewStatus?.toLowerCase?.() || reviewStatus;
};

export const parseReviewStatusFromFilters = (filters = []) => {
  const statusFilter = filters.find((f) => f.columnName === 'status');
  if (!statusFilter?.value) return undefined;

  const label = String(statusFilter.value).trim();
  return getReviewStatusKey(label);
};

// --- Query params ---

export const buildListQueryParams = ({
  page = 1,
  pageSize = 25,
  filters = [],
  lmsCode = LMS_CODE,
  queueView = QUEUE_VIEW.PENDING,
  searchTerm = '',
}) => {
  const params = new URLSearchParams({
    lmsCode,
    limit: String(pageSize),
    offset: String((page - 1) * pageSize),
  });

  const reviewStatusFromFilter = parseReviewStatusFromFilters(filters);

  if (reviewStatusFromFilter) {
    params.set('reviewStatus', reviewStatusFromFilter);
  } else if (queueView === QUEUE_VIEW.PENDING) {
    params.set('reviewStatus', REVIEW_STATUS.PENDING.key);
  }

  const trimmedSearch = String(searchTerm || '').trim();
  if (trimmedSearch.length >= 2) {
    params.set('search', trimmedSearch);
  }

  params.set('tags', SUGGESTED_RESOURCE_TAG);

  return params;
};

export const buildSearchQueryParams = (name, limit = 10) => {
  const params = new URLSearchParams({
    name: name.trim(),
    limit: String(limit),
  });
  params.set('tags', SUGGESTED_RESOURCE_TAG);
  return params;
};

/**
 * Normalize API tag values (string or { name, key, tag } objects).
 * @param {Array|undefined} tags
 * @returns {string[]}
 */
export const normalizeResourceTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  return tags
    .map((tag) => {
      if (typeof tag === 'string') return tag.trim();
      if (tag && typeof tag === 'object') {
        return String(tag.name ?? tag.key ?? tag.tag ?? tag.label ?? '').trim();
      }
      return '';
    })
    .filter(Boolean);
};

/**
 * True when resource has the course tag, or when tags are omitted (server already filtered).
 * @param {Object} resource
 * @returns {boolean}
 */
export const resourceHasSuggestedMatchTag = (resource) => {
  const tags = normalizeResourceTags(resource?.tags ?? resource?.metadata?.tags);
  if (tags.length === 0) return true;
  return tags.includes(SUGGESTED_RESOURCE_TAG);
};

// --- Resources ---

export const normalizeResourceId = (id) =>
  id == null || id === '' ? null : String(id);

// --- Dates ---

/** Pending item with a suggested resource key suitable for bulk link. */
export const isLinkableWithSuggestion = (item) =>
  item?.status === QUEUE_STATUS.PENDING &&
  item?.suggestedResource?.resourceKey != null &&
  resourceHasSuggestedMatchTag(item.suggestedResource);

/**
 * Map grid selection to queue items that can be bulk-linked via topSuggestion.
 * @param {Array} selectedRows - DataGrid selected row objects with rowData.id
 * @param {Array} items - Full queue items from the current page
 */
export const getLinkableItemsFromSelection = (selectedRows, items) => {
  const selectedIds = new Set(
    (selectedRows || []).map((row) => row.rowData?.id).filter((id) => id != null)
  );
  return items.filter((item) => selectedIds.has(item.id) && isLinkableWithSuggestion(item));
};

export const formatSuggestionScore = (score) =>
  score != null ? `${(score * 100).toFixed(0)}%` : '';

/**
 * Confidence tier for match score styling and bulk filters.
 * @param {number|null|undefined} score - 0–1 from API
 * @returns {'high'|'medium'|'low'|'none'}
 */
export const getSuggestionScoreTier = (score) => {
  if (score == null || Number.isNaN(score)) return SCORE_TIER.NONE;
  if (score >= SCORE_TIER_THRESHOLDS.HIGH) return SCORE_TIER.HIGH;
  if (score >= SCORE_TIER_THRESHOLDS.MEDIUM) return SCORE_TIER.MEDIUM;
  return SCORE_TIER.LOW;
};

export const isHighConfidenceSuggestion = (item) =>
  getSuggestionScoreTier(item?.suggestedResource?.score) === SCORE_TIER.HIGH;

/** Pending row with suggestion at or above the high-confidence threshold. */
export const isHighConfidenceLinkable = (item) =>
  isLinkableWithSuggestion(item) && isHighConfidenceSuggestion(item);

export const formatQueuedAt = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const formatLastRefreshedTime = (timestamp) => {
  if (!timestamp) return '';
  const options = { hour: 'numeric', minute: '2-digit', hour12: true };
  return `Last Refreshed ${timestamp.toLocaleTimeString('en-US', options)}`;
};
